import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import {
  DesignConfig,
  DesignTokenSet,
  PageScope,
  LIGHT_DEFAULTS,
  DARK_DEFAULTS,
  TOKEN_TO_CSS,
} from "@/lib/designTokenTypes";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useIsAdmin } from "@/hooks/useIsAdmin";

const STORAGE_KEY = "ayuni-design-tokens";

interface DesignTokenContextValue {
  /** Merged tokens for a given page and mode */
  getTokens: (page: PageScope, mode: "light" | "dark") => DesignTokenSet;
  /** Update a single token for a page scope + mode */
  setToken: (page: PageScope, mode: "light" | "dark", key: keyof DesignTokenSet, value: string) => void;
  /** Bulk-set tokens */
  setTokens: (page: PageScope, mode: "light" | "dark", partial: Partial<DesignTokenSet>) => void;
  /** Reset a page scope to defaults */
  resetPage: (page: PageScope) => void;
  /** Save current state to cloud */
  saveToCloud: () => Promise<void>;
  /** Whether cloud sync is in progress */
  isSaving: boolean;
  /** All overrides (for the panel) */
  allOverrides: Record<string, DesignConfig>;
  /** Current page scope being edited */
  editingScope: PageScope;
  setEditingScope: (s: PageScope) => void;
  /** Current mode being edited */
  editingMode: "light" | "dark";
  setEditingMode: (m: "light" | "dark") => void;
  /** Detected current page scope from route */
  currentPageScope: PageScope;
}

const DesignTokenContext = createContext<DesignTokenContextValue | null>(null);

export function useDesignTokens() {
  const ctx = useContext(DesignTokenContext);
  if (!ctx) throw new Error("useDesignTokens must be inside DesignTokenProvider");
  return ctx;
}

/** Detect page scope from pathname */
function detectPageScope(pathname: string): PageScope {
  if (pathname === "/" || pathname === "/home-editor") return "home";
  if (pathname.startsWith("/board-editor")) return "settings";  // MUST be before /board
  if (pathname.startsWith("/communication") || pathname.startsWith("/board")) return "communication";
  if (pathname.startsWith("/keyboard") || pathname.startsWith("/aac-keyboard")) return "keyboard";
  if (pathname.startsWith("/entertainment") || pathname.startsWith("/music") || pathname.startsWith("/spotify")) return "entertainment";
  if (pathname.startsWith("/settings")) return "settings";
  if (pathname.startsWith("/games")) return "games";
  if (pathname.startsWith("/radio")) return "radio";
  if (pathname.startsWith("/tv")) return "tv";
  if (pathname.startsWith("/youtube")) return "youtube";
  return "global";
}

/** Apply all CSS variables to :root based on current overrides and active theme */
function applyCSSVariables(overrides: Record<string, DesignConfig>, pathname: string) {
  const root = document.documentElement;
  const isDark = root.classList.contains("dark");
  const mode = isDark ? "dark" : "light";
  const defaults = isDark ? DARK_DEFAULTS : LIGHT_DEFAULTS;

  const currentPage = detectPageScope(pathname);

  // Merge: defaults < global overrides < page-specific overrides
  const globalOverrides = overrides["global"]?.[mode] || {};
  const pageOverrides = currentPage !== "global" ? (overrides[currentPage]?.[mode] || {}) : {};
  const merged = { ...defaults, ...globalOverrides, ...pageOverrides };

  // Apply each token as a CSS variable
  for (const [tokenKey, cssVar] of Object.entries(TOKEN_TO_CSS)) {
    const value = (merged as any)[tokenKey];
    if (value !== undefined) {
      root.style.setProperty(cssVar, value);
    }
  }
}

function loadFromStorage(): Record<string, DesignConfig> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveToStorage(data: Record<string, DesignConfig>) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}

export function DesignTokenProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const isAdmin = useIsAdmin();
  const [overrides, setOverrides] = useState<Record<string, DesignConfig>>(loadFromStorage);
  const overridesRef = useRef(overrides); // Always points to latest overrides
  const [isSaving, setIsSaving] = useState(false);
  const [editingScope, setEditingScope] = useState<PageScope>("global");
  const [editingMode, setEditingMode] = useState<"light" | "dark">("light");
  const initialLoadDone = useRef(false);
  const readyToSave = useRef(false);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const isFromRealtime = useRef(false);
  const isSavingRef = useRef(false);
  const [currentPathname, setCurrentPathname] = useState(window.location.pathname);

  // Keep ref in sync
  useEffect(() => { overridesRef.current = overrides; }, [overrides]);

  // Load global design defaults from cloud on mount (no auth needed)
  useEffect(() => {
    if (initialLoadDone.current) return;
    initialLoadDone.current = true;

    (async () => {
      try {
        const { data } = await (supabase as any)
          .from("global_design_defaults")
          .select("page_scope, config");

        if (data && (data as any[]).length > 0) {
          const cloudOverrides: Record<string, DesignConfig> = {};
          for (const row of data as any[]) {
            // Skip element-overrides — managed by ElementOverrideContext
            if (row.page_scope === 'element-overrides') continue;
            cloudOverrides[row.page_scope] = row.config as DesignConfig;
          }
          setOverrides(cloudOverrides);
          saveToStorage(cloudOverrides);
        }
      } finally {
        readyToSave.current = true;
      }
    })();
  }, []);

  // Realtime: listen for changes from other admins
  useEffect(() => {
    const channel = supabase
      .channel('global-design-tokens')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'global_design_defaults' },
        (payload: any) => {
          const row = payload.new;
          if (!row || row.page_scope === 'element-overrides') return;
          // Ignore realtime events triggered by our own saves
          if (isSavingRef.current || saveTimeoutRef.current) return;
          isFromRealtime.current = true;
          setOverrides(prev => {
            const next = { ...prev, [row.page_scope]: row.config as DesignConfig };
            saveToStorage(next);
            return next;
          });
        }
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, []);

  // Current page scope derived from pathname
  const currentPageScope = detectPageScope(currentPathname);

  // Re-apply CSS variables whenever overrides change or route changes or dark mode toggles
  useEffect(() => {
    applyCSSVariables(overrides, currentPathname);
  }, [overrides, currentPathname]);

  // Listen for dark mode class changes
  useEffect(() => {
    const observer = new MutationObserver(() => applyCSSVariables(overrides, currentPathname));
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, [overrides, currentPathname]);


  const getTokens = useCallback((page: PageScope, mode: "light" | "dark"): DesignTokenSet => {
    const defaults = mode === "dark" ? DARK_DEFAULTS : LIGHT_DEFAULTS;
    const globalO = overrides["global"]?.[mode] || {};
    const pageO = page !== "global" ? (overrides[page]?.[mode] || {}) : {};
    return { ...defaults, ...globalO, ...pageO };
  }, [overrides]);

  const setToken = useCallback((page: PageScope, mode: "light" | "dark", key: keyof DesignTokenSet, value: string) => {
    setOverrides(prev => {
      const next = { ...prev };
      if (!next[page]) next[page] = { light: {}, dark: {} };
      if (!next[page][mode]) next[page][mode] = {};
      (next[page][mode] as any)[key] = value;
      saveToStorage(next);
      return next;
    });
  }, []);

  const setTokens = useCallback((page: PageScope, mode: "light" | "dark", partial: Partial<DesignTokenSet>) => {
    setOverrides(prev => {
      const next = { ...prev };
      if (!next[page]) next[page] = { light: {}, dark: {} };
      next[page][mode] = { ...next[page][mode], ...partial };
      saveToStorage(next);
      return next;
    });
  }, []);

  const resetPage = useCallback((page: PageScope) => {
    setOverrides(prev => {
      const next = { ...prev };
      delete next[page];
      saveToStorage(next);
      return next;
    });
  }, []);

  const saveToCloud = useCallback(async () => {
    setIsSaving(true);
    isSavingRef.current = true;
    try {
      // CRITICAL: Use ref to always get the LATEST overrides, not the stale closure value
      const current = overridesRef.current;
      for (const [scope, config] of Object.entries(current)) {
        // Skip element-overrides — managed by ElementOverrideContext
        if (scope === 'element-overrides') continue;
        await (supabase as any)
          .from("global_design_defaults")
          .upsert({
            page_scope: scope,
            config,
            updated_at: new Date().toISOString(),
          }, { onConflict: "page_scope" });
      }
    } finally {
      setIsSaving(false);
      // Keep the guard active briefly so realtime echoes are ignored
      setTimeout(() => { isSavingRef.current = false; }, 3000);
    }
  }, []); // No dependency on overrides — uses ref instead

  // Auto-save to cloud on every change (debounced 1.5s)
  useEffect(() => {
    if (!readyToSave.current || !isAdmin) return;
    if (isFromRealtime.current) {
      isFromRealtime.current = false;
      return;
    }
    clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      saveTimeoutRef.current = undefined;
      saveToCloud();
    }, 1500);
    return () => clearTimeout(saveTimeoutRef.current);
  }, [overrides, isAdmin, saveToCloud]);

  // Listen for route changes via popstate and custom events
  useEffect(() => {
    const sync = () => setCurrentPathname(window.location.pathname);
    window.addEventListener("popstate", sync);
    // Also listen for pushState/replaceState via a custom event
    const origPush = history.pushState.bind(history);
    const origReplace = history.replaceState.bind(history);
    history.pushState = (...args: any[]) => { origPush(...args); sync(); };
    history.replaceState = (...args: any[]) => { origReplace(...args); sync(); };
    return () => {
      window.removeEventListener("popstate", sync);
      history.pushState = origPush;
      history.replaceState = origReplace;
    };
  }, []);

  return (
    <DesignTokenContext.Provider value={{
      getTokens,
      setToken,
      setTokens,
      resetPage,
      saveToCloud,
      isSaving,
      allOverrides: overrides,
      editingScope,
      setEditingScope,
      editingMode,
      setEditingMode,
      currentPageScope,
    }}>
      {children}
    </DesignTokenContext.Provider>
  );
}
